import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import isEqual from "lodash/isEqual";
import pick from "lodash/pick";
import { createSelectorCreator, defaultMemoize } from "reselect";

import { AccountBase, Folder, Maybe, ProjectPreview, Report, Store } from "fond/types";
import { getPath } from "fond/utils/folder";
import { useAppSelector } from "fond/utils/hooks";
import { Actions, mappings } from "fond/utils/permissions";
import { searchCollectionByKey } from "fond/utils/search";

import { selectCurrentAccount } from "./accountSlice";
import { apiSlice } from "./apiSlice";

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

export type GetFoldersResponse = {
  Items: Folder[];
};

export const foldersAdapter = createEntityAdapter<Folder>({
  selectId: (entity: Folder): string => entity.ID,
});
const foldersInitialState = foldersAdapter.getInitialState();

/**
 * Folders API Slice
 */
export const folderSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getFolder: build.query({
      query: (folderId: string) => `/v2/folders/${folderId}?expanded=true`,
      providesTags: (result) => (result ? [{ type: "Folder", id: result.ID }] : []),
    }),
    getFolders: build.query<ReturnType<typeof foldersAdapter.setAll>, { accountId?: string; expanded?: boolean } | void>({
      query: (params) => ({
        url: "/v2/folders",
        params: {
          expanded: params?.expanded ?? true,
          account_id: params?.accountId,
        },
      }),
      transformResponse: (response: GetFoldersResponse) => {
        return foldersAdapter.setAll(
          foldersInitialState,
          response.Items.map((folder) => ({ ...folder, EntityType: "folder" }))
        );
      },
      providesTags: (result, _error, params) => {
        const accountListId = `LIST:${params?.accountId || "all"}`;
        const listTags = [
          { type: "Folder" as const, id: "LIST" },
          { type: "Folder" as const, id: accountListId },
        ];
        return result ? [...result.ids.map((id) => ({ type: "Folder" as const, id })), ...listTags] : listTags;
      },
    }),
    createFolder: build.mutation<Folder, { AccountID: string } & Pick<Folder, "Name" | "Description" | "ParentID">>({
      query: (folder) => ({
        url: "/v2/folders",
        method: "POST",
        body: { AccountID: folder.AccountID, ...pick(folder, ["Name", "Description", "ParentID"]) },
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: "Folder", id: `LIST:${arg.AccountID}` }],
    }),
    updateFolder: build.mutation<Folder, Pick<Folder, "ID" | "Name" | "Description">>({
      query: ({ ID, ...initialFolder }) => ({
        url: `/v2/folders/${ID}`,
        method: "PUT",
        body: pick(initialFolder, ["Name", "Description"]),
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Folder", id: arg.ID }],
    }),
    moveFolder: build.mutation<Folder, Pick<Folder, "ID" | "ParentID">>({
      query: ({ ID, ParentID }) => ({
        url: `/v2/folders/${ID}`,
        method: "PUT",
        body: { ParentID },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Folder", id: arg.ID },
        { type: "Permission", id: "LIST" },
      ],
    }),
    deleteFolder: build.mutation<undefined, Folder>({
      query: ({ ID, ...initialFolder }) => ({
        url: `/v2/folders/${ID}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Folder", id: `LIST:${arg.Account.ID}` },
        { type: "Folder", id: arg.ID },
      ],
    }),
  }),
});

/**
 * Endpoint Hooks
 */
export const {
  useGetFolderQuery,
  useLazyGetFolderQuery,
  useGetFoldersQuery,
  useLazyGetFoldersQuery,
  useCreateFolderMutation,
  useDeleteFolderMutation,
  useUpdateFolderMutation,
  useMoveFolderMutation,
} = folderSlice;

/**
 * Custom hook to fetch folders for the current account from the URL.
 * This hook automatically passes the account ID from the URL to the query.
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useGetAccountFolders();
 * ```
 */
export const useGetAccountFolders = (): ReturnType<typeof useGetFoldersQuery> => {
  const currentAccount = useAppSelector(selectCurrentAccount);
  const accountId = currentAccount?.ID;
  return useGetFoldersQuery(accountId ? { accountId, expanded: true } : undefined, { skip: !accountId });
};

/**
 * Selectors
 */
const selectFoldersAccountId = (state: Store): string | undefined => {
  const currentAccount = selectCurrentAccount(state);
  return currentAccount?.ID;
};

const selectFoldersResult = createSelector([(state: Store) => state, selectFoldersAccountId], (state, accountId) => {
  return folderSlice.endpoints.getFolders.select(accountId ? { accountId, expanded: true } : undefined)(state);
});
const selectFoldersData = createSelector([selectFoldersResult], (foldersResult) => foldersResult?.data ?? foldersInitialState);

export const { selectAll: selectAllFolders, selectById: selectFolderById } = foldersAdapter.getSelectors(selectFoldersData);

/**
 * Returns all folders with the specified ParentID
 */
export const selectFoldersByParentId = createSelector(
  [selectAllFolders, (_state: Store, parentId: string | null | undefined) => parentId || null],
  (folders, parentId): Folder[] => {
    return folders.filter((folder) => folder.ParentID === parentId);
  }
);

/**
 * Returns all ancestor folders of an item
 */
export const getFolderAncestry = createSelector(
  [selectFoldersData, (_state: Store, folderId: string | undefined) => folderId],
  (folders, folderId) => {
    const parents: Folder[] = [];
    if (!folderId || !folders?.entities) return parents;

    const getFolder = (id: string) => {
      const parent: Folder | undefined = folders.entities[id];
      if (parent) {
        parents.push(parent);
        if (parent.ParentID) {
          getFolder(parent.ParentID);
        }
      }
    };

    getFolder(folderId);
    return parents;
  }
);

/**
 * Return folders within the specified account with the required permissions to perform Action.
 *
 * Generates the Path property matching the same permission
 *
 * MultiProject folders are filtered out as they are never a legal target of any action.
 */
const filterFolders = (folders: Folder[], action: Actions, accountId: Maybe<string>): Folder[] => {
  if (!accountId) return [];
  const allowedLevels = mappings.get(action);

  return folders
    .filter((folder) => allowedLevels?.includes(folder.Permission.Level) && folder.Account.ID === accountId && folder.MultiProject === null)
    .map((folder) => ({ ...folder, Path: getPath(folders, folder.ID) }));
};

/**
 * Return folders within the project's account with the required permissions.
 * This selector can take a project (for moving) or an account (for creating) as a parameter,
 * as the required permissions are the same for both actions.
 */
export const selectFoldersForAddingProject = createDeepEqualSelector(
  [
    selectAllFolders,
    (_state: Store, projectOrAccount: Maybe<AccountBase> | ProjectPreview): Maybe<string> => {
      if (projectOrAccount && "Account" in projectOrAccount) {
        return projectOrAccount.Account.ID;
      }
      return projectOrAccount?.ID;
    },
  ],
  (folders: Folder[], accountId: Maybe<string>): Folder[] => filterFolders(folders, Actions.PROJECT_ADD, accountId)
);

/**
 * Return folders within the report's account with the required permissions for moving a report.
 */
export const selectFoldersForReportMove = createDeepEqualSelector(
  [selectAllFolders, (_state: Store, report: Report) => report?.Account?.ID],
  (folders: Folder[], accountId: string): Folder[] => filterFolders(folders, Actions.REPORT_MOVE, accountId)
);

/**
 * Return folders within the folder's account with the required permissions for moving a folder.
 */
export const selectFoldersForFolderMove = createDeepEqualSelector(
  [selectAllFolders, (_state: Store, folder: Folder) => folder?.Account?.ID],
  (folders: Folder[], accountId: string): Folder[] => filterFolders(folders, Actions.FOLDER_ADD, accountId)
);

/**
 * Return folders within the folder's account with the required permissions for adding a city planner report.
 */
export const selectFoldersForMarketLensAdd = createDeepEqualSelector(
  [selectAllFolders, (_state: Store, account: Maybe<AccountBase>): Maybe<string> => account?.ID],
  (folders: Folder[], accountId: Maybe<string>): Folder[] => filterFolders(folders, Actions.CITY_PLANNER_ADD, accountId)
);

export const selectFoldersMatchingSearchKey = createDeepEqualSelector(
  [selectAllFolders, (_state: Store, searchKey: string) => searchKey],
  (folders: Folder[], searchKey: string): Folder[] => searchCollectionByKey(folders, searchKey, "Name")
);
